import ky from 'ky';
import type {
  StorePublicProduct, IncentiveResults, IncentiveCountry, IncentiveEquipment, ChargerConnectivity,
} from '@evi/shared';

/**
 * Shop API client. Base URL is the deployed Api (VITE_API_URL, injected by SST
 * per stage); in dev the Vite proxy forwards /api to localhost:3001. Every
 * public-shop call is unauthenticated: the catalog read and the guest checkout
 * (POST /store/checkout/guest) + lead intake (POST /public/leads) need no
 * session.
 */
const API_BASE = (import.meta.env['VITE_API_URL'] as string | undefined) ?? '/api';

export const api = ky.create({
  prefixUrl: API_BASE.replace(/\/$/, ''),
  retry: 1,
  timeout: 20_000,
});

/** Standard API envelope. */
export interface ApiResponse<T> {
  success: boolean;
  data: T;
  error?: { code: string; message: string; data?: unknown };
}

/** The public shop catalog: surfaces:'public' products (incl. DC), commission
 *  baked into DC prices. Unauthenticated. */
export async function fetchPublicCatalog(): Promise<StorePublicProduct[]> {
  const res = await api.get('store/catalog/public').json<ApiResponse<StorePublicProduct[]>>();
  return res.data;
}

export interface GuestCheckoutItem { product_id: string; quantity: number }

/** Start a guest Stripe Checkout session (server re-prices from the DB). On a
 *  4+ charger cart the server answers 422 BULK_QUOTE_REQUIRED; callers catch
 *  the ky HTTPError and read the body to route to the freight-quote flow. */
export async function startGuestCheckout(
  items: GuestCheckoutItem[], installLocationId?: string,
  /** A US ZIP: the server prices the freight live as one line, "Hardware shipped to location" (ruling B, 21 October 2026). */
  shipZip?: string,
): Promise<{ url: string; session_id: string }> {
  const res = await api
    .post('store/checkout/guest', { json: { items, ...(installLocationId ? { install_location_id: installLocationId } : {}), ...(shipZip ? { ship_to: { zip: shipZip, country: 'US' } } : {}) } })
    .json<ApiResponse<{ url: string; session_id: string }>>();
  return res.data;
}

/** Confirm a paid guest order from the success page's session_id. */
export async function confirmGuestOrder(
  sessionId: string,
): Promise<{ paid: boolean; order_number?: string; total_cents?: number }> {
  const res = await api
    .get('store/checkout/guest/confirm', { searchParams: { session_id: sessionId } })
    .json<ApiResponse<{ paid: boolean; order_number?: string; total_cents?: number }>>();
  return res.data;
}

export interface LeadSubmission {
  email: string;
  source: string;
  name?: string;
  phone?: string;
  tags?: string[];
  notes?: string;
  turnstile_token: string;
}

/** Public lead intake (POST /public/leads). Turnstile FAIL-CLOSED server-side:
 *  a missing/invalid token is a 403 (ky throws). Answers neutrally otherwise. */
export async function submitLead(body: LeadSubmission): Promise<void> {
  await api.post('public/leads', { json: body }).json<ApiResponse<{ ok: boolean }>>();
}

export interface PublicNetworkSubmission {
  contact: { name: string; email: string; phone?: string | null; role: string };
  location: {
    name: string;
    address: { street: string; city: string; state: string; zip: string; country?: string };
    type: string;
  };
  chargers: Array<{
    identity: string;
    vendor?: string | null;
    model?: string | null;
    connector_types: string[];
    max_power_kw?: number | null;
  }>;
  turnstile_token: string;
  /** Admin-issued magic-link token: the door. Without it the server
   *  answers 403 INVITE_REQUIRED. */
  invite_token: string;
}

/** Invite-only network-submission intake (POST /public/network-submissions).
 *  Consumes the single-use invite token; Turnstile stays FAIL-CLOSED on top.
 *  The response is neutral (the confirmation email is the receipt). */
export async function submitNetworkSubmissionPublic(
  body: PublicNetworkSubmission,
): Promise<void> {
  await api.post('public/network-submissions', { json: body }).json<ApiResponse<{ ok: boolean }>>();
}

/** A needs_info re-invite's prefill: the original submission's contact,
 *  location, chargers and connectivity, mirrored from the server's
 *  projectSubmissionPrefill (apps/api/src/lib/networkSubmissions.ts). The
 *  reviewer-side fields are never sent. */
export interface SubmissionInvitePrefill {
  contact: { name: string; email: string; phone: string | null; role: string };
  location: {
    name: string;
    address: { street: string; city: string; state: string; zip: string; country?: string };
    type: string;
  };
  chargers: Array<{
    identity: string;
    vendor: string | null;
    model: string | null;
    connector_types: string[];
    max_power_kw: number | null;
  }>;
  /** The deployment's recorded connectivity choice. Null (or absent from an
   *  older Api) when the submission predates the choice; the form then
   *  leaves the required choice empty. */
  connectivity?: ChargerConnectivity | null;
}

export type SubmissionInviteCheck =
  | { valid: false; reason: 'invalid' | 'used' | 'expired' | 'revoked' }
  | { valid: true; email: string; note: string | null; prefill: SubmissionInvitePrefill | null };

/** Check an invite link before rendering the form. Dead links come back
 *  with their honest reason; live ones carry the read-only recipient email
 *  and, on needs_info re-invites, the original submission as prefill. */
export async function validateSubmissionInvite(token: string): Promise<SubmissionInviteCheck> {
  const res = await api
    .get(`public/submission-invites/${encodeURIComponent(token)}`)
    .json<ApiResponse<SubmissionInviteCheck>>();
  return res.data;
}

// ── Public EVII (anonymous shop assistant) ───────────────────────────────────

/** One tool EVII ran while composing a reply (surfaced as inline status). */
export interface EvieToolCall {
  name: string;
  result_summary: string;
}

/** The public assistant reply envelope (data of POST /ai-assistant/public/message). */
export interface EvieReply {
  assistant_message: string;
  tool_calls_made: EvieToolCall[];
  /** Remaining messages in this anonymous session before the cap. */
  messages_left: number;
}

/** Parsed API error `{ code, message }` off a thrown ky HTTPError, or null when
 *  the failure carried no JSON envelope (network, non-JSON body). */
export async function readEvieError(
  e: unknown,
): Promise<{ code?: string; message?: string } | null> {
  const resp = (e as { response?: Response }).response;
  if (!resp) return null;
  try {
    const j = (await resp.json()) as { error?: { code?: string; message?: string } };
    return j.error ?? null;
  } catch {
    return null;
  }
}

/**
 * Send one message to the public shop EVII. `turnstile_token` is REQUIRED to
 * OPEN a session (fail-closed): pass it on the first message and again if the
 * server answers TURNSTILE_REQUIRED/TURNSTILE_FAILED (a lost/expired session).
 * Once open, the session flows without a token. `session_id` is client-minted
 * and held in localStorage. ky throws HTTPError on 4xx/5xx; callers read the
 * code via readEvieError to route Turnstile re-checks vs. plain errors.
 */
export async function postEvieMessage(body: {
  session_id: string;
  message: string;
  turnstile_token?: string;
}): Promise<EvieReply> {
  const res = await api
    .post('ai-assistant/public/message', { json: body })
    .json<ApiResponse<EvieReply>>();
  return res.data;
}

// ── Incentives (EV-charging incentive lookup) ────────────────────────────────

/** Fetch incentive programs for a country + region + equipment pick. US goes
 *  through the DSIRE proxy (may answer `unavailable` until the key is wired);
 *  Canada is the curated dataset. Federal programs come back first. */
export async function fetchIncentives(params: {
  country: IncentiveCountry;
  region: string;
  equipment: IncentiveEquipment;
  zip?: string;
}): Promise<IncentiveResults> {
  const searchParams: Record<string, string> = {
    country: params.country,
    region: params.region,
    equipment: params.equipment,
  };
  if (params.zip) searchParams['zip'] = params.zip;
  const res = await api
    .get('public/incentives', { searchParams })
    .json<ApiResponse<IncentiveResults>>();
  return res.data;
}

export { formatUsd } from './formatUsd';
